<script setup lang="ts">
/**
 * The web: every task on show as a node on a plane, left to arrange itself.
 *
 * A sub-task is tied to its parent, a task that waits on another is loosely
 * tied to it (dashed), and on a board filed by repo every tree is tied to a hub
 * for its repo, so related work clusters without anyone placing it. The
 * physics is lib/webPhysics.ts; this is the drawing and the pointer.
 *
 * It reads `board.shownTree`, the same tasks the tree shows, folded or not, so
 * "hide done" and "only mine" work here too. A tap opens the drawer beside it,
 * like a row would. Pan and zoom are kept per board, per browser; where the
 * nodes sit is not — it is the physics' to decide.
 */
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { dominantValue } from "@shared/rollup.js";
import { DONE_STATUSES, type Status, type Task } from "@shared/types.js";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import { bounds, createSim, reconcile, reheat, step, type NodeSpec, type PLink } from "../lib/webPhysics.js";

const board = useBoard();
const viewport = useViewport();

// ── the graph ────────────────────────────────────────────────────────────────

interface WebNode {
  id: string;
  kind: "task" | "hub";
  label: string;
  r: number;
  status: Status | null;
  urgency: number;
}
interface WebLink extends PLink {
  kind: "child" | "hub" | "blocks";
}

const HUB_PREFIX = "repo:";
const hubId = (repo: string | null): string => HUB_PREFIX + (repo ?? "");

const graph = computed(() => {
  const { filtered, undone, roots, byRepo } = board.shownTree;
  const rollups = board.rollups;
  const nodes: (WebNode & NodeSpec)[] = [];
  const links: WebLink[] = [];
  const hubs = new Map<string, WebNode & NodeSpec>();
  const shown = new Set<string>();
  const taskNode = (task: Task, near: string | null): WebNode & NodeSpec => {
    const r = rollups.get(task.id);
    return {
      id: task.id,
      kind: "task",
      label: task.title || "Untitled",
      // Bigger for more work underneath it, but never so big it swallows the rest.
      r: Math.min(26, 7 + 2.2 * Math.sqrt(r?.descendantCount ?? 0)),
      status: task.status,
      urgency: task.urgency,
      near,
    };
  };
  // Parents before children, so a new child is seated beside its parent.
  const walk = (task: Task, near: string | null): void => {
    nodes.push(taskNode(task, near));
    shown.add(task.id);
    for (const child of filtered.get(task.id) ?? []) {
      links.push({ kind: "child", a: task.id, b: child.id, len: 16, k: 0.5 });
      walk(child, task.id);
    }
  };
  for (const root of roots) {
    let near: string | null = null;
    if (byRepo) {
      // Filed as the tree files it: by the first repo found down the tree, before "only mine" cuts it.
      const repo = dominantValue(root, undone, (t) => t.repo);
      near = hubId(repo);
      if (!hubs.has(near)) {
        const hub = { id: near, kind: "hub" as const, label: repo ?? "no repo", r: 20, status: null, urgency: 0, near: null };
        hubs.set(near, hub);
        nodes.push(hub);
      }
      links.push({ kind: "hub", a: near, b: root.id, len: 50, k: 0.2 });
    }
    walk(root, near);
  }
  for (const id of shown) {
    for (const blocker of board.tasks.get(id)?.blockedBy ?? []) {
      if (shown.has(blocker)) links.push({ kind: "blocks", a: blocker, b: id, len: 90, k: 0.05 });
    }
  }
  return { nodes, links };
});

const sim = createSim();
/** Bumped every tick: the drawing reads positions off `sim`, which is not reactive. */
const frame = ref(0);

const drawn = computed(() => {
  void frame.value;
  const at = new Map<string, { x: number; y: number }>();
  for (const n of sim.nodes.values()) at.set(n.id, { x: n.x, y: n.y });
  const nodes = graph.value.nodes.flatMap((n) => {
    const p = at.get(n.id);
    return p ? [{ ...n, ...p }] : [];
  });
  const links = graph.value.links.flatMap((l) => {
    const a = at.get(l.a);
    const b = at.get(l.b);
    return a && b ? [{ key: `${l.kind}:${l.a}>${l.b}`, kind: l.kind, x1: a.x, y1: a.y, x2: b.x, y2: b.y }] : [];
  });
  return { nodes, links };
});

// ── the animation loop ───────────────────────────────────────────────────────

let raf: number | null = null;
/** True the first time the layout settles, so the view can be fitted to it once. */
let settledOnce = false;

function kick(): void {
  if (raf !== null || typeof requestAnimationFrame !== "function" || document.hidden) return;
  raf = requestAnimationFrame(tick);
}

function tick(): void {
  raf = null;
  // Two steps a frame: the layout settles in half the time and still looks smooth.
  let moving = step(sim);
  if (moving) moving = step(sim);
  frame.value++;
  if (moving || drag.value?.type === "node") kick();
  else if (!settledOnce) {
    settledOnce = true;
    if (!placed) fit();
  }
}

function onVisibility(): void {
  if (document.hidden) {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
  } else kick();
}

function shuffle(): void {
  for (const n of sim.nodes.values()) {
    n.vx += (Math.random() - 0.5) * 30;
    n.vy += (Math.random() - 0.5) * 30;
  }
  reheat(sim, 1);
  kick();
}

watch(
  graph,
  ({ nodes, links }) => {
    if (reconcile(sim, nodes, links)) kick();
    else frame.value++;
  },
  { immediate: true },
);

// ── pan and zoom, per board, per browser ─────────────────────────────────────

const el = ref<SVGSVGElement | null>(null);
const view = reactive({ x: 0, y: 0, z: 1 });
let placed = false;
const viewKey = (): string => `todos.web.view.${board.teamId ?? "personal"}`;

function loadView(): void {
  placed = false;
  try {
    const saved = JSON.parse(localStorage.getItem(viewKey()) ?? "null") as { x: number; y: number; z: number } | null;
    if (saved && [saved.x, saved.y, saved.z].every(Number.isFinite)) {
      Object.assign(view, saved);
      placed = true;
      return;
    }
  } catch {
    // Nothing kept: centre it, and fit once it settles.
  }
  const r = el.value?.getBoundingClientRect();
  Object.assign(view, { x: (r?.width ?? 0) / 2, y: (r?.height ?? 0) / 2, z: 1 });
}

let viewTimer: number | null = null;
function keepView(): void {
  placed = true;
  if (viewTimer !== null) window.clearTimeout(viewTimer);
  viewTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(viewKey(), JSON.stringify(view));
    } catch {
      // Private window, or storage full: it just will not be remembered.
    }
  }, 300);
}

/** Frame every node, with a margin. Not remembered, so it fits again next time. */
function fit(): void {
  const box = bounds(sim);
  const r = el.value?.getBoundingClientRect();
  if (!box || !r || !r.width || !r.height) return;
  const pad = 40;
  const z = Math.min(2, Math.max(0.15, Math.min((r.width - pad * 2) / (box.x1 - box.x0 || 1), (r.height - pad * 2) / (box.y1 - box.y0 || 1))));
  view.z = z;
  view.x = r.width / 2 - ((box.x0 + box.x1) / 2) * z;
  view.y = r.height / 2 - ((box.y0 + box.y1) / 2) * z;
}

function toWorld(cx: number, cy: number): { x: number; y: number } {
  const r = el.value!.getBoundingClientRect();
  return { x: (cx - r.left - view.x) / view.z, y: (cy - r.top - view.y) / view.z };
}

function zoomAt(cx: number, cy: number, z: number): void {
  const r = el.value!.getBoundingClientRect();
  const next = Math.min(4, Math.max(0.1, z));
  const px = cx - r.left;
  const py = cy - r.top;
  view.x = px - ((px - view.x) / view.z) * next;
  view.y = py - ((py - view.y) / view.z) * next;
  view.z = next;
  keepView();
}

function onWheel(e: WheelEvent): void {
  // ctrl is also what a trackpad pinch sends.
  if (e.ctrlKey || e.metaKey) zoomAt(e.clientX, e.clientY, view.z * Math.exp(-e.deltaY * 0.01));
  else {
    view.x -= e.deltaX;
    view.y -= e.deltaY;
    keepView();
  }
}

// ── the pointer ──────────────────────────────────────────────────────────────

type Drag =
  | { type: "node"; id: string; sx: number; sy: number; moved: boolean }
  | { type: "pan"; sx: number; sy: number; x0: number; y0: number; moved: boolean }
  | { type: "pinch"; dist: number; z: number };

const drag = ref<Drag | null>(null);
const pointers = new Map<number, { x: number; y: number }>();
const hover = ref<string | null>(null);
/** A press that travels less than this is a tap. */
const SLOP = 4;

function onPointerDown(e: PointerEvent): void {
  if (e.button !== 0 && e.pointerType === "mouse") return;
  pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  try {
    el.value?.setPointerCapture(e.pointerId);
  } catch {
    // A pointer the browser no longer knows: the drag just ends at the edge.
  }
  if (pointers.size === 2) {
    // A second finger turns whatever the first was doing into a pinch.
    const held = drag.value?.type === "node" ? sim.nodes.get(drag.value.id) : undefined;
    if (held) held.fixed = false;
    const [a, b] = [...pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
    drag.value = { type: "pinch", dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, z: view.z };
    return;
  }
  const id = (e.target as Element).closest?.("[data-node-id]")?.getAttribute("data-node-id");
  drag.value = id
    ? { type: "node", id, sx: e.clientX, sy: e.clientY, moved: false }
    : { type: "pan", sx: e.clientX, sy: e.clientY, x0: view.x, y0: view.y, moved: false };
}

function onPointerMove(e: PointerEvent): void {
  if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  const d = drag.value;
  if (!d) return;
  if (d.type === "pinch") {
    if (pointers.size < 2) return;
    const [a, b] = [...pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
    zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, d.z * (Math.hypot(a.x - b.x, a.y - b.y) / d.dist));
    return;
  }
  if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < SLOP) return;
  d.moved = true;
  if (d.type === "pan") {
    view.x = d.x0 + e.clientX - d.sx;
    view.y = d.y0 + e.clientY - d.sy;
    keepView();
    return;
  }
  const n = sim.nodes.get(d.id);
  if (!n) return;
  const at = toWorld(e.clientX, e.clientY);
  n.fixed = true;
  n.x = at.x;
  n.y = at.y;
  // Its neighbours follow it.
  reheat(sim, 0.3);
  kick();
}

function onPointerUp(e: PointerEvent): void {
  pointers.delete(e.pointerId);
  const d = drag.value;
  if (!d) return;
  if (d.type === "pinch") {
    if (pointers.size === 0) drag.value = null;
    return;
  }
  drag.value = null;
  if (d.type === "node") {
    const n = sim.nodes.get(d.id);
    if (n) n.fixed = false;
    if (d.moved) kick();
    else if (!d.id.startsWith(HUB_PREFIX)) board.select(d.id);
  } else if (!d.moved && board.selectedId) {
    board.select(null);
  }
}

// ── drawing ──────────────────────────────────────────────────────────────────

/** Titles appear once zoomed in far enough to read them, and always for the hubs, the hovered and the selected. */
const showLabel = (n: WebNode): boolean =>
  n.kind === "hub" || view.z >= 0.9 || n.id === hover.value || n.id === board.selectedId;

const clip = (s: string): string => (s.length > 32 ? s.slice(0, 31) + "…" : s);

const empty = computed(() => graph.value.nodes.length === 0);

watch(
  () => board.teamId,
  () => {
    loadView();
    settledOnce = false;
  },
);

onMounted(() => {
  loadView();
  document.addEventListener("visibilitychange", onVisibility);
  kick();
});

onBeforeUnmount(() => {
  if (raf !== null) cancelAnimationFrame(raf);
  raf = null;
  if (viewTimer !== null) window.clearTimeout(viewTimer);
  document.removeEventListener("visibilitychange", onVisibility);
});

defineExpose({ sim });
</script>

<template>
  <div class="web-view">
    <div class="pane-head">
      <span>Web</span>
      <span style="flex: 1"></span>
      <label class="head-check" title="Hide finished tasks (a done parent stays while anything under it is open)">
        <input type="checkbox" v-model="viewport.hideDone" />
        hide done
      </label>
      <label class="head-check" title="Only tasks assigned to you, and the parents they sit in">
        <input type="checkbox" v-model="viewport.onlyMine" />
        only mine
      </label>
      <button class="btn ghost" type="button" title="Shake the web up and let it settle again" @click="shuffle">shuffle</button>
      <button class="btn ghost" type="button" title="Fit every task on screen" @click="fit">fit</button>
    </div>
    <svg
      ref="el"
      class="web-canvas"
      :class="{ panning: drag?.type === 'pan' && drag.moved }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @wheel.prevent="onWheel"
    >
      <g :transform="`translate(${view.x} ${view.y}) scale(${view.z})`">
        <line
          v-for="l in drawn.links"
          :key="l.key"
          class="web-link"
          :class="'web-link-' + l.kind"
          :x1="l.x1"
          :y1="l.y1"
          :x2="l.x2"
          :y2="l.y2"
        />
        <g
          v-for="n in drawn.nodes"
          :key="n.id"
          class="web-node"
          :class="[
            n.kind === 'hub' ? 'web-hub' : 'st-' + n.status,
            { selected: n.id === board.selectedId, done: n.status && DONE_STATUSES.has(n.status) },
          ]"
          :data-node-id="n.id"
          :transform="`translate(${n.x} ${n.y})`"
          @pointerenter="hover = n.id"
          @pointerleave="hover === n.id && (hover = null)"
        >
          <circle v-if="n.kind === 'task' && n.urgency >= 3" class="web-urgency" :r="n.r + 2 + n.urgency" />
          <circle class="web-dot" :r="n.r" />
          <text v-if="showLabel(n)" class="web-label" :y="n.r + 12" :font-size="12 / Math.max(view.z, 0.6)">
            {{ clip(n.label) }}
          </text>
          <title>{{ n.label }}</title>
        </g>
      </g>
    </svg>
    <p v-if="empty" class="web-empty">Nothing to show. Add a task on the Board and it will turn up here.</p>
  </div>
</template>
